import React from 'react';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { SelectionToolbarPlugin } from '@atlaskit/editor-plugins/selection-toolbar';

import { CompanyHubBannerLoader } from '@confluence/company-hub/entry-points/CompanyHubBanner';
import {
	EditorControlsBannerLoader,
	LivePagesFeedbackBannerLoader,
	useIsControlsBannerDismissed,
} from '@confluence/editor-action-bar';
import type { TriggerAnalyticsEventType } from '@confluence/editor-features';
import { fg } from '@confluence/feature-gating';
import { EditorDistinctionBanner } from '@confluence/live-pages-features/entry-points/EditorDistinctionBanner';

export type Props = {
	isLivePage: boolean;
	isEmbeddedConfluence: boolean;
	viewMode?: boolean;
	hasEditPermission: boolean;
	shouldRenderPagePreview: boolean;
	spaceKey: string;
	isUnpublishedDraft: boolean;
	isCompanyHub: boolean;
	isABTestOptedInLivePageReleasePhase: boolean;
	editorFullPagePresetAPI: PublicPluginAPI<[SelectionToolbarPlugin]> | undefined;
	triggerAnalyticsEvent?: TriggerAnalyticsEventType;
};

export const FullPageEditorBannerWrapper = ({
	isLivePage,
	isEmbeddedConfluence,
	viewMode,
	hasEditPermission,
	shouldRenderPagePreview,
	spaceKey,
	isUnpublishedDraft,
	isCompanyHub,
	isABTestOptedInLivePageReleasePhase,
	triggerAnalyticsEvent,
}: Props) => {
	const { isControlsBannerDismissed, onDismiss, bannerType } =
		useIsControlsBannerDismissed(isLivePage);

	if (!viewMode && !isControlsBannerDismissed) {
		return (
			<EditorControlsBannerLoader
				onDismiss={onDismiss}
				triggerAnalyticsEvent={triggerAnalyticsEvent}
				isLivePage={isLivePage}
				isEmbeddedConfluence={isEmbeddedConfluence}
				bannerType={bannerType}
			/>
		);
	}

	if (isLivePage) {
		if (!fg('confluence_live_pages_ab_test_opted_in')) {
			return <LivePagesFeedbackBannerLoader isViewOnly={viewMode && !hasEditPermission} />;
		}
	} else {
		if (!shouldRenderPagePreview) {
			return <CompanyHubBannerLoader spaceKey={spaceKey} />;
		}
	}

	if (!isLivePage && !isUnpublishedDraft && !isCompanyHub && isABTestOptedInLivePageReleasePhase) {
		return <EditorDistinctionBanner />;
	}
};
